import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Loader2, Upload, X, Plus } from 'lucide-react';
import { PageHeader } from "@/components/shared/PageHeader";

const categories = [
  'Electronics',
  'Fashion & Clothing',
  'Home & Garden',
  'Beauty & Health',
  'Sports & Outdoors',
  'Toys & Games',
  'Books & Media',
  'Automotive',
  'Food & Beverages',
  'Office Supplies',
  'Jewelry & Accessories',
  'Pet Supplies',
];

export default function EditProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [productData, setProductData] = useState({
    title: '',
    description: '',
    price: '',
    category: '',
    stock: '',
    status: 'active' as 'active' | 'draft',
    images: [] as string[],
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [variations, setVariations] = useState<Array<{ name: string; value: string }>>([]);
  const [sizeType, setSizeType] = useState<'clothing' | 'shoes'>('clothing');

  const clothingSizes = ["XS", "S", "M", "L", "XL", "XXL", "2XL", "3XL"];
  const shoeSizes = ["36", "37", "38", "39", "40", "41", "42", "43", "44", "45", "46", "47", "48"];
  
  const isFashionCategory = productData.category === "Fashion & Clothing" || productData.category === "Jewelry & Accessories";
  const availableSizes = sizeType === 'shoes' ? shoeSizes : clothingSizes;

  const toggleSize = (size: string) => {
    setSelectedSizes(prev => 
      prev.includes(size) 
        ? prev.filter(s => s !== size)
        : [...prev, size]
    );
  };
  
  // Reset sizes when size type changes
  const handleSizeTypeChange = (type: 'clothing' | 'shoes') => {
    setSizeType(type);
    setSelectedSizes([]);
  };

  const addVariation = () => {
    setVariations(prev => [...prev, { name: "", value: "" }]);
  };

  const updateVariation = (index: number, field: "name" | "value", value: string) => {
    setVariations(prev => prev.map((v, i) => 
      i === index ? { ...v, [field]: value } : v
    ));
  };

  const removeVariation = (index: number) => {
    setVariations(prev => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (user && id) {
      fetchProduct();
    }
  }, [user, id]);

  const fetchProduct = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .eq('seller_id', user?.id)
        .single();

      if (error) throw error;

      if (data) {
        setProductData({
          title: data.title,
          description: data.description || '',
          price: data.price.toString(),
          category: data.category || '',
          stock: data.stock?.toString() || '0',
          status: data.status as 'active' | 'draft',
          images: data.images || [],
        });
        setSelectedSizes((data as any).sizes || []);
        setVariations((data as any).variations || []);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      toast({
        title: 'Error',
        description: 'Failed to load product details',
        variant: 'destructive',
      });
      navigate('/seller/catalog');
    } finally {
      setFetching(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}-${Date.now()}.${fileExt}`;

    setUploadingImage(true);
    try {
      // Upload to products bucket
      const { error: uploadError, data } = await supabase.storage
        .from('products')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('products')
        .getPublicUrl(fileName);

      setProductData(prev => ({
        ...prev,
        images: [...prev.images, publicUrl],
      }));

      toast({
        title: 'Success',
        description: 'Image uploaded successfully',
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload image',
        variant: 'destructive',
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (index: number) => {
    setProductData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !id) return;

    // Validation
    if (!productData.title || !productData.price || !productData.category) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('products')
        .update({
          title: productData.title,
          description: productData.description,
          price: parseFloat(productData.price),
          category: productData.category,
          stock: parseInt(productData.stock) || 0,
          status: productData.status,
          images: productData.images,
          sizes: selectedSizes,
          variations: variations.filter(v => v.name && v.value),
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('seller_id', user.id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Product updated successfully',
      });

      navigate('/seller/catalog');
    } catch (error) {
      console.error('Error updating product:', error);
      toast({
        title: 'Error',
        description: 'Failed to update product',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <Card>
          <CardContent className="p-12">
            <div className="flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <PageHeader
        title="Edit Product"
        description="Update your product information and details"
        backTo="/seller/catalog"
      />

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Product Images */}
            <div className="space-y-2">
              <Label>Product Images</Label>
              <div className="grid grid-cols-3 gap-4">
                {productData.images.map((image, index) => (
                  <div key={index} className="relative aspect-square">
                    <img
                      src={image}
                      alt={`Product ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2"
                      onClick={() => removeImage(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {productData.images.length < 5 && (
                  <label className="relative aspect-square border-2 border-dashed border-muted-foreground/25 rounded-lg cursor-pointer hover:border-primary/50 transition-colors flex items-center justify-center">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="sr-only"
                      disabled={uploadingImage}
                    />
                    {uploadingImage ? (
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    ) : (
                      <Upload className="h-8 w-8 text-muted-foreground" />
                    )}
                  </label>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                Upload up to 5 images. First image will be the main product image.
              </p>
            </div>

            {/* Product Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Product Title *</Label>
              <Input
                id="title"
                value={productData.title}
                onChange={(e) => setProductData({ ...productData, title: e.target.value })}
                placeholder="Enter product title"
                required
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={productData.description}
                onChange={(e) => setProductData({ ...productData, description: e.target.value })}
                placeholder="Describe your product"
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Price */}
              <div className="space-y-2">
                <Label htmlFor="price">Price (₦) *</Label>
                <Input
                  id="price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={productData.price}
                  onChange={(e) => setProductData({ ...productData, price: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>

              {/* Stock */}
              <div className="space-y-2">
                <Label htmlFor="stock">Stock Quantity</Label>
                <Input
                  id="stock"
                  type="number"
                  min="0"
                  value={productData.stock}
                  onChange={(e) => setProductData({ ...productData, stock: e.target.value })}
                  placeholder="0"
                />
              </div>
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select
                value={productData.category}
                onValueChange={(value) => setProductData({ ...productData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Product Sizes */}
            <div className="space-y-2">
              <Label>Available Sizes (Optional)</Label>
              
              {/* Size Type Toggle for Fashion Categories */}
              {isFashionCategory && (
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => handleSizeTypeChange('clothing')}
                    className={`px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
                      sizeType === 'clothing'
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background border-input hover:bg-accent hover:text-accent-foreground"
                    }`}
                  >
                    Clothing Sizes
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSizeTypeChange('shoes')}
                    className={`px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
                      sizeType === 'shoes'
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background border-input hover:bg-accent hover:text-accent-foreground"
                    }`}
                  >
                    Shoe Sizes (EU)
                  </button>
                </div>
              )}
              
              <div className="flex flex-wrap gap-2">
                {availableSizes.map((size) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => toggleSize(size)}
                    className={`px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
                      selectedSizes.includes(size)
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background border-input hover:bg-accent hover:text-accent-foreground"
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">
                {isFashionCategory 
                  ? `Select ${sizeType === 'shoes' ? 'shoe' : 'clothing'} sizes available for this product`
                  : 'Select all sizes available for this product'
                }
              </p>
            </div>

            {/* Product Variations */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Product Variations (Optional)</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addVariation}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Variation
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                Add variations like colors, materials, or styles
              </p>
              {variations.length > 0 && (
                <div className="space-y-3 mt-3">
                  {variations.map((variation, index) => (
                    <div key={index} className="flex gap-3 items-start">
                      <div className="flex-1">
                        <Input
                          placeholder="Type (e.g., Color, Material)"
                          value={variation.name}
                          onChange={(e) => updateVariation(index, "name", e.target.value)}
                        />
                      </div>
                      <div className="flex-1">
                        <Input
                          placeholder="Value (e.g., Red, Cotton)"
                          value={variation.value}
                          onChange={(e) => updateVariation(index, "value", e.target.value)}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        onClick={() => removeVariation(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Status */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="status">Product Status</Label>
                <p className="text-sm text-muted-foreground">
                  Active products are visible to buyers
                </p>
              </div>
              <Switch
                id="status"
                checked={productData.status === 'active'}
                onCheckedChange={(checked) =>
                  setProductData({ ...productData, status: checked ? 'active' : 'draft' })
                }
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/seller/catalog')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Product'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}